import { TCustomAxiosError } from '@/services/api';
import { TPeerSupportContactInfo } from '@/types/PeerSupport.types';
import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import useToast from '../useToast';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { requestPeerSupportContactPost } from '@/services/api/requests/peer-support';
import { mixpanelEvents } from '@/constants/mixpanel';

const useRequestPeerSupportContact = ({
  onSuccessCallback,
}: {
  onSuccessCallback?: () => void;
}): {
  isError: boolean;
  isPending: boolean;
  isSuccess: boolean;
  mutate: UseMutateFunction<TPeerSupportContactInfo, TCustomAxiosError, TPeerSupportContactInfo, unknown>;
} => {
  const toast = useToast();
  const { trackEvent } = useTelemetry();

  const { mutate, isPending, isError, isSuccess } = useMutation({
    mutationFn: async ({ peerSupportId, contactMethod }: TPeerSupportContactInfo) => {
      await requestPeerSupportContactPost({
        peerSupportId,
        contactMethod,
      });

      return { peerSupportId, contactMethod };
    },
    onSuccess: (peerSupportContactInfo: TPeerSupportContactInfo) => {
      trackEvent(
        peerSupportContactInfo.contactMethod === 'call'
          ? mixpanelEvents.PEER_SUPPORT_CONTACTED_VIA_PHONE
          : mixpanelEvents.PEER_SUPPORT_CONTACTED_VIA_TEXT,
        {
          peerSupportId: peerSupportContactInfo.peerSupportId,
        },
      );

      onSuccessCallback?.();
    },
    onError: (error: TCustomAxiosError) => {
      toast({
        status: 'error',
        title: 'Contact error',
        description: error.errors[0].message || 'An error occurred while trying to contact Peer Support.',
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};

export default useRequestPeerSupportContact;
